import React, { useEffect } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import routes from "./routes";
import Login from "../features/auth/Login";
import Logout from "../features/auth/Logout";
import TopPage from "../pages/TopPage";

import { isAuthenticatedSelector } from "../recoil/selectors/authSelector";
import MainLayout from "../layouts/MainLayout";
import PendingReceipt from "../pages/PendingReceipt";
import ProductRergistration from "../pages/ProductRegistration";
import PendingPackaging from "../pages/PendingPackaging";
import PendingPlan from "../pages/PendingPlan";
import PendingShipment from "../pages/PendingShipment";
import Shipped from "../pages/Shipped";
import NeedsReview from "../pages/NeedsReview";
import PrintLabelsPage from "../components/LabelPrintPage";
import AllList from "../pages/AllList";
import ManagementUsers from "../pages/ManagementUsers";
import Management from "../pages/ManagementAdmins";
import BaseList from "../pages/BaseList";
import Report from "../pages/Report";
import SignUp from "../features/auth/SignUp";
import ResetPassword from "../pages/ResetPassword";
import UserInfo from "../pages/UserInfo";
import MultiPrintLabelsPage from "../components/MultiPrintLabelsPage";
import TrustBox from "../pages/TrustBox";
import PrintExpirationLabelsPage from "../components/PrintExpirationLabelsPage";
import { authAtom } from "../recoil/atoms/authAtom";
import { loginUserAtom } from "../recoil/atoms/loginUserAtoms";
import { userSearchOptionsAtom } from "../recoil/atoms/userSearchOptionsAtoms";
import { useApiGetUsersSearchOptions } from "../hooks/api/useApiGetUserSearchOptions";
import { useApiGetBaseSearchOptions } from "../hooks/api/useApiGetBaseSearchOptions";
import { useApiGetStatusList } from "../hooks/api/useApiGetStatusList";
import { baseSearchOptionsAtom } from "../recoil/atoms/baseSearchOptionsAtoms";
import { statusListAtom } from "../recoil/atoms/statusListAtoms";
import { ResponseUserSearchOptionsResponse } from "../services/OpenAPI";
import AmazonCallback from "../pages/AmazonCallbak";
import { useApiGetTransportationOptions } from "../hooks/api/useApiGetTransportationOptions";
import { transportationOptionsState } from "../recoil/atoms/transportationOptionsAtoms";
import { warehousesState } from "../recoil/atoms/warehousesAtoms";
import { useApiGetWarehouses } from "../hooks/api/useApiGetWarehouses";
import Setting from "../pages/Setting";
import Privacy from "../pages/Privacy";
import Terms from "../pages/Terms";
import OperationLogs from "../pages/OperationLogs";
import {
  isBaseLevel,
  isGeneralUser,
  isNotOwner,
  isOwner,
} from "../constants/roles";

const AppRouter = () => {
  const isAuthenticated = useRecoilValue(isAuthenticatedSelector);
  const location = useLocation(); // Router 内にいるので useLocation はそのまま使える

  const setAuth = useSetRecoilState(authAtom);
  const setLoginUser = useSetRecoilState(loginUserAtom);
  const setUserSearchOptions = useSetRecoilState(userSearchOptionsAtom);
  const setBaseSearchOptions = useSetRecoilState(baseSearchOptionsAtom);
  const setStatusList = useSetRecoilState(statusListAtom);
  const setTransportationOptionsState = useSetRecoilState(
    transportationOptionsState
  );
  const setWarehousesState = useSetRecoilState(warehousesState);

  const { fetchUserSearchOptions } = useApiGetUsersSearchOptions();
  const { fetchBaseSearchOptions } = useApiGetBaseSearchOptions();
  const { fetchStatusList } = useApiGetStatusList();
  const { fetchTransportationOptions } = useApiGetTransportationOptions();
  const { fetchWarehouses } = useApiGetWarehouses();

  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (!userStr) return;

    const user = JSON.parse(userStr);

    setAuth({
      isAuthenticated: true,
      user: { mailAddress: user.mailAddress },
    });
    setLoginUser({ user });

    // ユーザー選択肢取得
    if (user.baseId && user.permissionId && user.tenantId) {
      const request = {
        baseId: user.baseId,
        permissionId: user.permissionId,
        tenantId: user.tenantId,
      };

      fetchUserSearchOptions(request).then((options) => {
        let filtered: ResponseUserSearchOptionsResponse[] = [];

        if (isOwner(user.permissionId)) {
          filtered = options?.filter((u) => u.id !== 0) ?? [];
        } else if (isBaseLevel(user.permissionId)) {
          filtered =
            options?.filter(
              (u) =>
                u.id !== 0 && u.baseId === user.baseId && isNotOwner(u.permissionId)
            ) ?? [];
        } else if (isGeneralUser(user.permissionId)) {
          filtered = options?.filter((u) => u.id === user.id) ?? [];
        }

        setUserSearchOptions({ userSearchOptions: filtered });
      });
    }

    // 拠点選択肢取得
    fetchBaseSearchOptions().then((bases) => {
      let filtered = bases ?? [];

      if (isNotOwner(user.permissionId)) {
        const allowedBaseIds = (user.baseInfo ?? []).map(
          (b: { id: number }) => b.id
        );
        filtered = bases?.filter((b) => allowedBaseIds.includes(b.id!)) ?? [];
      }

      setBaseSearchOptions({ baseSearchOptions: filtered });
    });

    // ステータス一覧取得
    fetchStatusList().then((statuses) => {
      const filtered = statuses?.filter((s) => (s.id ?? 0) < 8) ?? [];
      setStatusList({ statusList: filtered });
    });

    // 配送方法一覧取得
    fetchTransportationOptions().then((transOptions) => {
      setTransportationOptionsState(transOptions);
    });

    // 倉庫一覧取得
    fetchWarehouses().then((warehouses) => setWarehousesState(warehouses));
  }, []);

  return (
    <Routes>
      <Route path="/" element={<Navigate to={routes.login} replace />} />
      <Route path={routes.login} element={<Login />} />
      <Route path={routes.signUp} element={<SignUp />} />
      <Route path={routes.resetPassword} element={<ResetPassword />} />
      <Route
        path={routes.toppage}
        element={
          isAuthenticated ? (
            <MainLayout>
              <TopPage />
            </MainLayout>
          ) : (
            <Navigate to={routes.login} state={{ from: location }} replace />
          )
        }
      />
      <Route path={routes.logout} element={<Logout />} />
      {/* 商品登録 */}
      <Route
        path={routes.productRergistration}
        element={
          <MainLayout>
            <ProductRergistration />
          </MainLayout>
        }
      />
      {/* 入荷待ち */}
      <Route
        path={routes.pendingReceipt}
        element={
          <MainLayout>
            <PendingReceipt />
          </MainLayout>
        }
      />
      {/* 梱包待ち */}
      <Route
        path={routes.pendingPackaging}
        element={
          <MainLayout>
            <PendingPackaging />
          </MainLayout>
        }
      />
      {/* プラン作成待ち */}
      <Route
        path={routes.pendingPlan}
        element={
          <MainLayout>
            <PendingPlan />
          </MainLayout>
        }
      />
      {/* 出荷待ち */}
      <Route
        path={routes.pendingShipment}
        element={
          <MainLayout>
            <PendingShipment />
          </MainLayout>
        }
      />
      {/* 出荷済み */}
      <Route
        path={routes.shipped}
        element={
          <MainLayout>
            <Shipped />
          </MainLayout>
        }
      />
      {/* 要確認 */}
      <Route
        path={routes.needsReview}
        element={
          <MainLayout>
            <NeedsReview />
          </MainLayout>
        }
      />
      {/* 一覧 */}
      <Route
        path={routes.allList}
        element={
          <MainLayout>
            <AllList />
          </MainLayout>
        }
      />
      {/* ごみ箱 */}
      <Route
        path={routes.trustBox}
        element={
          <MainLayout>
            <TrustBox />
          </MainLayout>
        }
      />
      {/* ユーザ管理 */}
      <Route
        path={routes.managementUsers}
        element={
          <MainLayout>
            <ManagementUsers />
          </MainLayout>
        }
      />
      {/* 管理者 */}
      <Route
        path={routes.management}
        element={
          <MainLayout>
            <Management />
          </MainLayout>
        }
      />
      {/* 拠点一覧 */}
      <Route
        path={routes.baseList}
        element={
          <MainLayout>
            <BaseList />
          </MainLayout>
        }
      />
      {/* レポート */}
      <Route
        path={routes.report}
        element={
          <MainLayout>
            <Report />
          </MainLayout>
        }
      />
      {/* ユーザー情報 */}
      <Route
        path={routes.userInfo}
        element={
          <MainLayout>
            <UserInfo />
          </MainLayout>
        }
      />
      {/* 設定 */}
      <Route
        path={routes.setting}
        element={
          <MainLayout>
            <Setting />
          </MainLayout>
        }
      />
      {/* 操作ログ */}
      <Route
        path={routes.operationLogs}
        element={
          <MainLayout>
            <OperationLogs />
          </MainLayout>
        }
      />
      {/* AmazonCallbak画面 */}
      <Route path={routes.amazonCallbak} element={<AmazonCallback />} />
      <Route
        path={routes.printLabel}
        element={
          <PrintLabelsPage
            fnsku={new URLSearchParams(location.search).get("fnsku") || ""}
            itemName={
              new URLSearchParams(location.search).get("itemName") || ""
            }
            count={parseInt(
              new URLSearchParams(location.search).get("count") || "1"
            )}
          />
        }
      />
      <Route path={routes.printLabels} element={<MultiPrintLabelsPage />} />
      <Route
        path={routes.printExpirationLabels}
        element={<PrintExpirationLabelsPage />}
      />
      <Route path={routes.privacy} element={<Privacy />} />
      <Route path={routes.notFound} element={<Terms />} />
      <Route path={routes.notFound} element={<div>Not Found</div>} />
    </Routes>
  );
};

export default AppRouter;
